/**
 * providers/ninetailed/ninetailed-provider.tsx
 *
 * Env-gated mount of the Ninetailed `NinetailedProvider`. When no public Client
 * ID is configured (`NEXT_PUBLIC_NINETAILED_CLIENT_ID` unset) the provider is NOT
 * mounted and children render exactly as today.
 *
 * Must be rendered inside both `ConfigProvider` (preview mode) and `UserProvider`
 * (login state for the trait sync).
 *
 * Consent revoke (granted -> denied) is handled here by REMOUNTING the SDK:
 * `<NinetailedProvider>` is keyed on the consent state, so flipping consent tears
 * down the personalized SDK instance and builds a fresh one. Before that remount
 * we synchronously purge the SDK's localStorage (`purgePersonalizationStorage`),
 * including its consent flag, so the fresh SDK reads no stale consent/profile and
 * starts in the strict blocked state — reverting every `<Experience>` under it to
 * the baseline live, with no `__nt_*` left behind and no page reload. Grant is
 * still re-resolved by `<NinetailedConsentBridge>` (which also remounts, resetting
 * its transition ref, and re-fires the resolving events after consent settles).
 */

'use client';

import React, { useMemo, useRef } from 'react';
// Use the base React provider, NOT `@ninetailed/experience.js-next`. The `-next`
// provider wraps this same provider with an internal <Tracker> that calls
// `useRouter()` from `next/router` (Pages Router), which throws
// "NextRouter was not mounted" in the App Router. We do App Router page-view
// tracking ourselves via <TrackPages> (usePathname), so we only need the base
// provider here. All other SDK exports (`useNinetailed`, `Experience`) are
// re-exported by `-next` from this same package, so they are unaffected.
import { NinetailedProvider } from '@ninetailed/experience.js-react';

import {
  buildNinetailedOptions,
} from '@/lib/ninetailed/buildNinetailedOptions';
import {
  NINETAILED_CLIENT_ID,
  NINETAILED_ENVIRONMENT,
  NINETAILED_FORCE_PREVIEW,
  isNinetailedConfigured,
} from '@/lib/ninetailed/env';
import { purgePersonalizationStorage } from '@/lib/ninetailed/personalization-storage';
import { buildContentfulEntryUrl } from '@/lib/ninetailed/contentfulEntryUrl';
import { usePersonalizationConsent } from '@/lib/consent/use-personalization-consent';
import { useGetPreviewMode } from '@/providers/ConfigProvider';

import { NinetailedTraitSync } from './ninetailed-trait-sync';
import { TrackPages } from './track-pages';
import { NinetailedConsentBridge } from './ninetailed-consent-bridge';
import { useNinetailedPreviewCatalog } from './use-ninetailed-preview-catalog';

// Contentful space/environment for the preview widget's "Go to editor" deep-links.
// Read as literal `process.env.NEXT_PUBLIC_*` references so Next.js inlines them
// into the client bundle (the same vars apolloClient.ts uses for the GraphQL host).
const CONTENTFUL_SPACE_ID = process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID;
const CONTENTFUL_ENVIRONMENT = process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT;

export function NinetailedExperienceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const genericPreview = useGetPreviewMode();
  const hasConsent = usePersonalizationConsent();

  // Ninetailed preview is forced ON in local development
  // (`NEXT_PUBLIC_ENV=development`); in every other environment it follows the
  // generic Contentful preview state. See lib/ninetailed/env.ts.
  // const previewMode = NINETAILED_FORCE_PREVIEW || genericPreview;
  const previewMode = genericPreview;

  // Preview widget catalog (all experiences + audiences). Fetches only in preview
  // mode; returns an empty, already-loaded catalog otherwise (no network). Feeds
  // the preview plugin so its editor panels aren't empty. See the hook for why.
  const catalog = useNinetailedPreviewCatalog(previewMode);

  // The preview PLUGIN is added only once the catalog has loaded. The plugin
  // attaches a single global widget container on init and REFUSES to rebind if
  // one is already attached (it logs "Preview plugin is already attached." and
  // marks itself inactive). So building it first with an empty catalog and then
  // rebuilding with the full one would leave the widget stuck on the empty
  // snapshot. Gating on `catalog.loaded` means the plugin is constructed exactly
  // once, with the full catalog, and attaches its widget a single time. The SDK
  // itself still runs in preview from first mount (via the `preview` prop below).
  const previewPluginReady = previewMode && catalog.loaded;

  // Client ID + environment come from NEXT_PUBLIC_* env vars (deployment-global),
  // not tenant/Contentful config. See lib/ninetailed/env.ts.
  const clientId = NINETAILED_CLIENT_ID;
  // NOTE: NinetailedProvider instantiates the SDK once (internal useMemo with
  // empty deps), so this memo just avoids rebuilding the options/plugins array
  // on re-render — provider props are captured at first mount and preview mode
  // cannot be toggled without a remount (fine: it is stable per request). The
  // catalog is a dep so the preview plugin is rebuilt with the fetched catalog;
  // the mount key below turns that rebuild into a single remount.
  const options = useMemo(
    () =>
      buildNinetailedOptions(
        { clientId: NINETAILED_CLIENT_ID, environment: NINETAILED_ENVIRONMENT },
        {
          previewMode: previewPluginReady,
          previewExperiences: catalog.experiences,
          previewAudiences: catalog.audiences,
          // "Go to editor": the widget hands us only the Ninetailed id, so we map
          // it back to the Contentful entry sys.id (from the catalog) and open the
          // entry in the Contentful web app. Supplying these also clears the
          // widget's "not fully set up to support Go to editor" warning. Guarded:
          // a missing mapping / space id yields a null URL and we no-op.
          onOpenExperienceEditor: (experience) => {
            const url = buildContentfulEntryUrl(
              CONTENTFUL_SPACE_ID,
              CONTENTFUL_ENVIRONMENT,
              catalog.entryIds.experienceEntryIdByNtId[experience.id],
            );
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
          },
          onOpenAudienceEditor: (audience) => {
            const url = buildContentfulEntryUrl(
              CONTENTFUL_SPACE_ID,
              CONTENTFUL_ENVIRONMENT,
              catalog.entryIds.audienceEntryIdByNtId[audience.id],
            );
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
          },
        },
      ),
    [previewPluginReady, catalog],
  );

  // Remount the SDK only when consent is REVOKED. `usePersonalizationConsent`
  // starts deny-by-default and settles to the real value on mount, and grant is
  // already re-resolved live by `<NinetailedConsentBridge>` — so remounting on
  // every consent change (initial settle, grant) would needlessly tear down the
  // whole subtree. Instead we bump a generation only on the granted -> denied
  // edge and, in the same render, purge the SDK's localStorage BEFORE the keyed
  // `<NinetailedProvider>` below remounts. The fresh SDK reads `__nt-consent__`
  // as it initializes (in the child's render, strictly after this parent render),
  // so purging here guarantees it sees no consent (starts blocked) and no stale
  // profile caches. Purge + guard are idempotent, so StrictMode's double render
  // bumps the generation at most once.
  const revokeGenRef = useRef(0);
  const wasConsentedRef = useRef(hasConsent);
  if (wasConsentedRef.current && !hasConsent) {
    purgePersonalizationStorage();
    revokeGenRef.current += 1;
  }
  wasConsentedRef.current = hasConsent;

  // Tenant gate: no client key -> render children unchanged (baseline, no SDK).
  if (!clientId) {
    return <>{children}</>;
  }

  return (
    <NinetailedProvider
      // Key changes after a revoke (see above) OR once the preview catalog
      // finishes loading, which is when the preview plugin is added (see
      // `previewPluginReady`). Remounting rebuilds the SDK so the newly-added
      // plugin — and only one widget attach — takes effect. `catalog.loaded` is
      // true from the start outside preview, so this is a no-op there. Grant is
      // handled without a remount by the consent bridge.
      key={`nt-gen-${revokeGenRef.current}-cat-${catalog.loaded ? 1 : 0}`}
      clientId={options.clientId}
      environment={options.environment}
      plugins={options.plugins}
      requestTimeout={options.requestTimeout}
      componentViewTrackingThreshold={options.componentViewTrackingThreshold}
      preview={previewMode}
    >
      <NinetailedConsentBridge />
      <NinetailedTraitSync />
      <TrackPages />
      {children}
    </NinetailedProvider>
  );
}

/**
 * True only when the Ninetailed SDK is actually mounted (a public Client ID is
 * configured via `NEXT_PUBLIC_NINETAILED_CLIENT_ID`) — mirroring the gate in
 * `NinetailedExperienceProvider` above.
 *
 * Consumers MUST check this before rendering `<Experience>`: that component
 * calls `useNinetailed()` unconditionally and THROWS when no `NinetailedProvider`
 * is in the tree (the state of every deployment without a Client ID). Gating the
 * mount on data alone (e.g. "this entry has experiences") would crash the page
 * on the supposedly-inert path.
 */
export function useIsNinetailedActive(): boolean {
  return isNinetailedConfigured;
}
